import { useQuery } from '@tanstack/react-query';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { DashboardPatient, fetchDashboardPatients } from '../../services/patient';
import { isV2PatientVersion } from '@odo/shared-ui';
import { useUser } from '../UserProvider';

export type PatientListItemType = DashboardPatient;

interface PatientListContextType {
    patients: DashboardPatient[];
    isLoading: boolean;
    isFetching: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    reset: () => void;
    /**
     * True only when the practice's patients span BOTH flow versions. The v1/v2
     * pill is noise for a practice that is wholly on one version, so callers use
     * this to decide whether to render it at all.
     */
    hasMixedPatientVersions: boolean;
}

const Ctx = createContext<PatientListContextType | undefined>(undefined);

export function usePatientList(): PatientListContextType {
    const context = useContext(Ctx);
    if (context === undefined) {
        throw new Error('usePatientList must be used within a PatientListProvider');
    }
    return context;
}

interface PatientListProviderProps {
    children: ReactNode;
    /** 'always' (default) forces a network refetch every time this provider
     *  mounts, ignoring staleTime — right for landing on the patients page,
     *  wrong for secondary consumers of the shared cache (e.g. the global
     *  appointments modal's patient picker, whose mount was refreshing the
     *  overview table underneath it). Those pass false: cached data is served
     *  as-is, and the initial fetch still happens when the cache is empty. */
    refetchOnMount?: boolean | 'always';
}

export function PatientListProvider({
    children,
    refetchOnMount = 'always'
}: PatientListProviderProps) {
    const { user, setUnreadCount, getUnreadCount } = useUser();
    const practiceIds = user?.database.data.practices;

    const {
        data: patients = [],
        isLoading,
        isFetching,
        error: queryError,
        refetch
    } = useQuery({
        queryKey: ['patients', 'dashboard', practiceIds],
        queryFn: fetchDashboardPatients,
        enabled: !!practiceIds?.length,
        staleTime: 15 * 60 * 1000,
        refetchInterval: 15 * 60 * 1000,
        refetchOnMount
    });

    const error = queryError
        ? queryError instanceof Error
            ? queryError.message
            : 'Failed to fetch patients'
        : null;

    const refresh = useCallback(async () => {
        await refetch();
    }, [refetch]);

    const reset = useCallback(() => {
        refetch();
    }, [refetch]);

    // Keep the sidebar "action required" badge in sync with the loaded list.
    useEffect(() => {
        const unreadCount = patients.filter((patient) => {
            const action = patient.requiredAction;
            return action && action !== 'None';
        }).length;
        if (unreadCount !== getUnreadCount()) {
            setUnreadCount(unreadCount);
        }
    }, [patients, setUnreadCount, getUnreadCount]);

    // Derived from the already-loaded list: /patients/mine returns the whole
    // practice-scoped set unpaginated, so no extra request is needed.
    const hasMixedPatientVersions = useMemo(() => {
        let sawV1 = false;
        let sawV2 = false;
        for (const patient of patients) {
            if (isV2PatientVersion(patient.version)) sawV2 = true;
            else sawV1 = true;
            if (sawV1 && sawV2) return true;
        }
        return false;
    }, [patients]);

    const value = useMemo<PatientListContextType>(
        () => ({
            patients,
            isLoading,
            isFetching,
            error,
            refresh,
            reset,
            hasMixedPatientVersions
        }),
        [patients, isLoading, isFetching, error, refresh, reset, hasMixedPatientVersions]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
